/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {GenericObject} from '@ion/core/src/types/entities';

/**
 * Defines the Auth Config which is used when retrieving the temporary token
 * used for initiating the underlying WebSocket handshake.
 */
export type PreAuthTokenConfig = {
  websocketTokenURL: string,
  appKey: string
}

/**
 * Defines the  Auth Token Response which provides the temporary authentication
 * token which is to be used when initiating the underlying WebSocket handshake.
 */
export type PreAuthTokenResponse = {
  response: {
    errors: [],
    results: [{
      authToken: string
    }]
  }
}

/**
 * Defines the default Socket interface which establishes the public
 * API available to all socket clients.
 */
export interface SocketConnection {
  get isConnected(): boolean;
  initialize(options: SocketConfiguration, force?: boolean): void;
  subscribe: Subscription
  unsubscribe: Subscription
  _destroy: () => void;
}

/**
 * Defines the instantiation options required by SocketConnection instances.
 */
export interface SocketConfiguration {
  url: string;
  authToken: string;
  preauthToken?: string,
  appKey: string;
  reconnectionInterval?: number;
  onConnect?: (event: Event) => void;
  onDisconnect?: (event: CloseEvent) => void;
  onConnectionError?: (error: ErrorEvent) => void;
  onData?: OnDataEvent;
  onReconnection?: (attempt: number) => void;
  onSubscription?: (results: SubscriptionResults) => void;
  disableAutoReconnect?: boolean;
  disableKeepAlive?: boolean;
}

/**
 * Defines the WebSocket subscription types used for registering and
 * un-registering subscriptions to and from the underlying service.
 */
export enum SubscriptionAction {
  subscribe = 'subscribe',
  unsubscribe = 'unsubscribe'
}

/**
 * Defines the types of Entity's which can be subscribed to, currently
 * this includes Assets and Tasks (bulk operations).
 */
export enum EntitySubscription {
  asset = 'asset',
  task = 'task'
}

/**
 * Defines the valid observable entity identifier types, which in some cases
 * are of type number, while in others they are of type string.
 */
export type ObservableEntityId = number | string;

/**
 * Defines the valid observable entity identifier for individual or multiple
 * ids.
 */
export type ObservableEntityIds = ObservableEntityId | ObservableEntityId[];

/**
 * Defines the Entity Subscription Result type which provides a list of
 * successfully added observer entity ids, and failed entity ids (if any).
 *
 * This type is generally only used internally by SocketConnection instances,
 * and a SubscriptionResults object is then created from a SubscriptionResponse
 * and returned to clients.
 *
 * @see SubscriptionResponse
 */
export type SubscriptionResponse = {
  message: {
    [id in EntitySubscription]?: {
      results: {
        success: {
          ids: ObservableEntityId[]
        }
      },
      errors: {
        failure: {
          ids: ObservableEntityId[]
        }
      }
    }
  } | string
}

/**
 * Defines the consolidated data type parsed and returned from a received
 * `SubscriptionResponse`.
 */
export type SubscriptionResults = {
  succeeded: ObservableEntityId[],
  failed: ObservableEntityId[],
  entity: EntitySubscription,
}

/**
 * Defines the subscription function type used by both the subscribe and
 * unsubscribe methods of a Socket instance.
 */
export type Subscription = (
  entity: EntitySubscription, ids: ObservableEntityIds) => boolean;

/**
 * Defines the Function type definition for an `onData` callback, whereby the
 * data argument is the parsed JSON message received.
 *
 * The onDataEvent can be invoked with either an InboundMessage (change to an
 * entity), or with a SubscriptionResponse (containing the results of adding
 * or removing an entity subscription).
 */
export type OnDataEvent = (
  data: GenericObject, id?: ObservableEntityId) => void;

/**
 * Defines the connection state of the underlying WebSocket instance.
 *
 *  [0] 'CONNECTING' : Socket has been created. The connection is not yet open.
 *  [1] 'OPEN'       : The connection is open and ready to communicate.
 *  [2] 'CLOSING'    : The connection is in the process of closing.
 *  [3] 'CLOSED'     : The connection is closed or couldn't be opened.
 */
export enum ConnectionStatus {
  'CONNECTING',
  'OPEN',
  'CLOSING',
  'CLOSED'
}

/**
 * Defines the Observer Map type definition whereby each id has a collection
 * of OnDataEvent function associations which will be invoked upon receiving
 * a message for the given id.
 */
export type ObserverMap = {
  [entity in EntitySubscription]: {
    [id in ObservableEntityId]: OnDataEvent[];
  }
}

/**
 * Defines the state change for an observable entity incoming change event
 * message.
 */
export interface StateChange {
  source: string,
  data: {
    label: string,
    value: string,
    units?: string,
    source: string,
    deviceId: string
  }
}

/**
 * Defines the type for entity update messages received by the underlying
 * WebSocket Connect. Updates to the form of an entity attribute name as
 * the key, with the value being an object containing a source and data
 * property.
 *
 * The data property contains information about the change; generally, the
 * current value of the change, and in some cases, an object containing a
 * value property (which, as the name implies, contains the value) as well
 * as additional information unique to the given attribute.
 *
 * The source property represents the source which triggered the change.
 *
 * ```typescript
 * {
 *   "change": {
 *     "latitude" : {
 *       "source" : 'MessagePrecedence'
 *       "data" : {
 *         "label"    : 'latitude',
 *         "value"    : '50.5569135',
 *         "units"    : null,
 *         "source"   : 'LMU',
 *         "deviceId" : '420343'
 *       },
 *     },
 *     "odometer": {
 *       "source": "MessagePrecedence",
 *       "data" : {
 *         "label"  : "Odometer",
 *         "value"  : "4621.15",
 *         "units"  : "mi",
 *         "source" : "OEM"
 *       }
 *     }
 *  }
 * ```
 */
export interface EntityUpdate {
  [key: string]: StateChange | any;
}

/**
 * Defines the message type for an incoming Asset Change, which implements
 * the standard EntityMessage with additional properties unique to Assets,
 * such as `assetId`, etc.
 */
export interface AssetMessage {
  assetStateChange: {
    assetId: number
    lastMessageTime: number
    lastMessageDeviceType: string
    change: EntityUpdate
  }
}

/**
 * Defines the type for an inbound message from the underlying WebSocket
 * Service API.
 *
 * An inbound message can consist of an object which contains a top-level
 * entity state change key (e.g. "assetStateChange"), which contains the
 * id of the entity, the `lastMessageTime`, and a `change` property. The
 * change property provides top-level keys representing each attribute
 * that has changed. The value changes are generally provided under the
 * `data.value` property, though, in some cases, the `data` property will
 * represent the change in value.
 *
 * ```typescript
 *
 * {
 *   "message": {
 *     "assetStateChange": {
 *       "assetId": 395744,
 *       "lastMessageDeviceType": 'LMU',
 *       "lastMessageTime": 1643291190712,
 *       "change": {
 *         "latitude" : {
 *           "source" : 'MessagePrecedence',
 *           "data" : {
 *             "label"    : 'latitude',
 *             "value"    : '50.5569135',
 *             "units"    : null,
 *             "source"   : 'LMU',
 *             "deviceId" : '420343'
 *           }
 *         },
 *         "odometer": {
 *           "source": "MessagePrecedence",
 *           "data" : {
 *             "label"  : "Odometer",
 *             "value"  : "4621.15",
 *             "units"  : "mi",
 *             "source" : "OEM"
 *           }
 *         },
 *         "dtcCodes" : {
 *           "source" : "OEM_DTC",
 *           "data" : [{
 *             "dtcCode"         : "bmw_CCM_143",
 *             "description"     : "Tyre pressure loss. Caution stop Status: ZERO Distance Remaining: 105592",
 *             "type"            : null,
 *             "typeDescription" : "OEM:BMW"
 *           }],
 *         }
 *       }
 *     }
 *   }
 * }
 * ```
 */
export interface InboundMessage {
  message: AssetMessage | SubscriptionResponse
}
